import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import Button from '@/components/base/Button';
import { publicApi } from '@/utils/api';
import { ApiError } from '@/utils/apiError';
import { formatDate } from '@/utils/format';
import PasswordInput from '@/components/base/PasswordInput';

/**
 * The creator side of joining campaigns, shared by an agency's join page (`/join/:ref`, scoped to
 * that agency) and the sign-in page's Creator tab (every agency): New on Twiva? / Already on Twiva?
 * → pick campaigns → done.
 *
 * This portal is the agencies' dashboard, so the creator's join token lives only in this component's
 * memory: it's never stored and never signs anyone in to the dashboard.
 */

/** A campaign the signed-in creator can join (`GET …/campaigns`). */
interface JoinableCampaign {
  id: string;
  name: string;
  brand: string;
  agency: string;
  summary: string;
  deliverables: string;
  contentTypes: string[];
  markets: string[];
  startDate: string;
  endDate: string;
  inviteOnly: boolean;
  invited: boolean;
  joined: boolean;
}

/** The campaign whose link brought the creator here, if it's still open. */
export interface FeaturedCampaign {
  id: string;
  name: string;
  inviteOnly: boolean;
}

type Step = 'choose' | 'register' | 'login' | 'campaigns' | 'done';
interface Session {
  token: string;
  name: string;
}

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);
const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

interface CreatorFlowProps {
  /** API path for this flow: `/join/agencies/<ref>` or `/join/creators`. */
  base: string;
  /** Set on an agency's page; absent on the sign-in page (campaigns from every agency). */
  agencyName?: string;
  featured?: FeaturedCampaign | null;
  /** Shown above the two choices on the first step. */
  intro: ReactNode;
  /** Called whenever the step changes, so the page can adapt its header. */
  onStepChange?: (step: Step) => void;
}

export default function CreatorFlow({ base, agencyName, featured = null, intro, onStepChange }: CreatorFlowProps) {
  const [step, setStepState] = useState<Step>('choose');
  const [session, setSession] = useState<Session | null>(null);
  const [joined, setJoined] = useState<string[]>([]);
  const [notice, setNotice] = useState('');

  const setStep = (next: Step) => {
    setStepState(next);
    onStepChange?.(next);
  };
  const signedIn = (next: Session) => {
    setSession(next);
    setNotice('');
    setStep('campaigns');
  };
  const signedOut = (message = '') => {
    setSession(null);
    setJoined([]);
    setNotice(message);
    setStep('choose');
  };

  return (
    <div>
      <Steps current={step === 'campaigns' ? 1 : step === 'done' ? 2 : 0} />
      <div className="mt-5">
        {step === 'choose' ? (
          <div>
            {notice ? <Alert tone="info">{notice}</Alert> : null}
            {intro}
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <ChoiceCard icon="ri-user-add-line" title="New on Twiva?" subtitle="Create your creator account" onClick={() => setStep('register')} />
              <ChoiceCard icon="ri-login-box-line" title="Already on Twiva?" subtitle="Sign in to your account" onClick={() => setStep('login')} />
            </div>
          </div>
        ) : step === 'register' || step === 'login' ? (
          <AccountForm base={base} mode={step} onMode={setStep} onBack={() => setStep('choose')} onSignedIn={signedIn} />
        ) : step === 'campaigns' && session ? (
          <CampaignPicker
            base={base}
            session={session}
            agencyName={agencyName}
            featured={featured}
            onJoined={(name) => setJoined((names) => (names.includes(name) ? names : [...names, name]))}
            onDone={() => setStep('done')}
            onExpired={() => signedOut('Your sign-in timed out. Please sign in again.')}
          />
        ) : (
          <Done agencyName={agencyName} name={session?.name ?? ''} joined={joined} onSignOut={() => signedOut()} />
        )}
      </div>
    </div>
  );
}

const STEP_LABELS = ['Your account', 'Pick campaigns', 'Done'];

function Steps({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label="Progress">
      {STEP_LABELS.map((label, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'next';
        return (
          <li key={label} className="flex flex-1 items-center gap-2" aria-current={state === 'current' ? 'step' : undefined}>
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-semibold ${
                state === 'next' ? 'bg-background-200 text-foreground-500' : 'bg-primary-600 text-background-50'
              }`}
            >
              {state === 'done' ? <i className="ri-check-line" /> : index + 1}
            </span>
            <span className={`truncate ${state === 'current' ? 'font-semibold text-foreground-950' : 'text-foreground-500'}`}>{label}</span>
            {index < STEP_LABELS.length - 1 ? <span className="h-px flex-1 bg-background-300" aria-hidden="true" /> : null}
          </li>
        );
      })}
    </ol>
  );
}

function ChoiceCard({ icon, title, subtitle, onClick }: { icon: string; title: string; subtitle: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-lg text-primary-600 group-hover:bg-background-50">
        <i className={icon} aria-hidden="true" />
      </span>
      <span className="flex-1">
        <span className="block text-sm font-semibold text-foreground-950">{title}</span>
        <span className="block text-xs text-foreground-600">{subtitle}</span>
      </span>
      <i className="ri-arrow-right-s-line text-lg text-foreground-400 group-hover:text-primary-600" aria-hidden="true" />
    </button>
  );
}

function AccountForm({
  base,
  mode,
  onMode,
  onBack,
  onSignedIn,
}: {
  base: string;
  mode: 'register' | 'login';
  onMode: (step: Step) => void;
  onBack: () => void;
  onSignedIn: (session: Session) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const register = mode === 'register';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (register && password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = register ? { name, email, phone, password } : { email, password };
      const reply = await publicApi<{ token: string; creator: { name: string } }>(`${base}/${mode}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onSignedIn({ token: reply.token, name: reply.creator.name });
    } catch (submitError) {
      setError(messageOf(submitError, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = () => {
    setError('');
    setPassword('');
    setConfirm('');
    onMode(register ? 'login' : 'register');
  };

  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-foreground-600 hover:text-foreground-900">
        <i className="ri-arrow-left-line" /> Back
      </button>
      <h2 className="font-heading text-lg font-semibold text-foreground-950">{register ? 'Create your Twiva account' : 'Welcome back'}</h2>
      <p className="mt-1 text-sm text-foreground-600">
        {register ? 'One account for every Twiva campaign, and for the Twiva app.' : 'Sign in with the email and password you use in the Twiva app.'}
      </p>

      <div className="mt-5 space-y-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {register ? (
          <Field label="Full name">
            <input required autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </Field>
        ) : null}
        <Field label="Email" hint={register ? 'Use the one your invitation was sent to, if you have one.' : undefined}>
          <input
            type="email"
            required
            autoComplete="email"
            autoFocus={!register}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
        </Field>
        {register ? (
          <Field label="Phone number">
            <input type="tel" required autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0712 345 678" className={inputClass} />
          </Field>
        ) : null}
        <Field label="Password" hint={register ? 'At least 8 characters.' : undefined}>
          <PasswordInput
            required
            minLength={register ? 8 : undefined}
            autoComplete={register ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        {register ? (
          <Field label="Confirm password">
            <PasswordInput required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass} />
          </Field>
        ) : null}
      </div>

      <Button type="submit" size="lg" className="mt-6 w-full" disabled={submitting}>
        {submitting ? (register ? 'Creating your account…' : 'Signing in…') : register ? 'Create account' : 'Sign in'}
      </Button>
      <p className="mt-4 text-center text-sm text-foreground-600">
        {register ? 'Already on Twiva? ' : 'New on Twiva? '}
        <button type="button" onClick={switchMode} className="font-medium text-primary-700 hover:text-primary-900">
          {register ? 'Sign in' : 'Create an account'}
        </button>
      </p>
    </form>
  );
}

function CampaignPicker({
  base,
  session,
  agencyName,
  featured,
  onJoined,
  onDone,
  onExpired,
}: {
  base: string;
  session: Session;
  agencyName?: string;
  featured: FeaturedCampaign | null;
  onJoined: (name: string) => void;
  onDone: () => void;
  onExpired: () => void;
}) {
  const [campaigns, setCampaigns] = useState<JoinableCampaign[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const handleError = useCallback(
    (failure: unknown, fallback: string) => {
      if (failure instanceof ApiError && failure.status === 401) onExpired();
      else setError(messageOf(failure, fallback));
    },
    [onExpired],
  );

  useEffect(() => {
    publicApi<JoinableCampaign[]>(`${base}/campaigns`, { headers: { Authorization: `Bearer ${session.token}` } })
      // The campaign whose link brought them here comes first.
      .then((list) => setCampaigns([...list].sort((a, b) => Number(b.id === featured?.id) - Number(a.id === featured?.id))))
      .catch((failure: unknown) => handleError(failure, 'Could not load the campaigns.'));
  }, [base, session.token, featured?.id, handleError]);

  const join = async (campaign: JoinableCampaign) => {
    setBusyId(campaign.id);
    setError('');
    try {
      await publicApi(`${base}/campaigns/${encodeURIComponent(campaign.id)}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.token}` },
      });
      setCampaigns((list) => list?.map((c) => (c.id === campaign.id ? { ...c, joined: true, invited: false } : c)) ?? list);
      onJoined(campaign.name);
    } catch (failure) {
      handleError(failure, 'Could not join this campaign.');
    } finally {
      setBusyId('');
    }
  };

  const featuredMissing = featured?.inviteOnly && campaigns && !campaigns.some((c) => c.id === featured.id);
  const who = agencyName ?? 'The agency';

  return (
    <div>
      <h2 className="font-heading text-lg font-semibold text-foreground-950">Hi {firstName(session.name)}, pick your campaigns</h2>
      <p className="mt-1 text-sm text-foreground-600">
        Join the ones that suit you. {agencyName ? `${agencyName} will` : "Each campaign's agency will"} send you its brief and fee to accept.
      </p>

      <div className="mt-4 space-y-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {featuredMissing ? (
          <Alert tone="info">
            {featured.name} is by invitation only, and this account isn't on its invite list. If you were invited with a different email address,
            sign in with that one instead.
          </Alert>
        ) : null}
        {!campaigns ? (
          <p className="py-6 text-center text-sm text-foreground-500">Loading campaigns…</p>
        ) : campaigns.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 px-4 py-8 text-center">
            <i className="ri-inbox-line text-2xl text-foreground-400" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-foreground-800">No campaigns are open for you right now</p>
            <p className="mt-1 text-xs text-foreground-600">
              Your Twiva account is ready. {agencyName ? `${who} can invite you to its next campaign.` : 'Agencies can invite you to their next campaigns.'}
            </p>
          </div>
        ) : (
          campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              showAgency={!agencyName}
              featured={campaign.id === featured?.id}
              busy={busyId === campaign.id}
              onJoin={() => void join(campaign)}
            />
          ))
        )}
      </div>

      <Button size="lg" variant="outline" className="mt-6 w-full" onClick={onDone}>
        {campaigns?.some((c) => c.joined) ? 'Done' : "I'll do this later"}
      </Button>
    </div>
  );
}

function CampaignCard({
  campaign,
  showAgency,
  featured,
  busy,
  onJoin,
}: {
  campaign: JoinableCampaign;
  showAgency: boolean;
  featured: boolean;
  busy: boolean;
  onJoin: () => void;
}) {
  const dates = [campaign.startDate, campaign.endDate].filter(Boolean).map(formatDate).join(' – ');
  const facts = [dates, campaign.markets.join(', '), campaign.contentTypes.join(', ')].filter(Boolean);
  const byline = [campaign.brand ? `For ${campaign.brand}` : '', showAgency ? `by ${campaign.agency}` : ''].filter(Boolean).join(' · ');
  return (
    <div className={`rounded-lg border p-4 ${featured ? 'border-accent-300 bg-accent-50/40' : 'border-background-200 bg-background-50'}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-sm font-semibold text-foreground-950">{campaign.name}</h3>
            {campaign.invited || campaign.inviteOnly ? (
              <Badge icon="ri-mail-star-line" tone="accent">Invited</Badge>
            ) : (
              <Badge icon="ri-team-line" tone="neutral">Open</Badge>
            )}
          </div>
          {byline ? <p className="text-xs text-foreground-600">{byline}</p> : null}
          {facts.length ? <p className="mt-1 text-xs text-foreground-500">{facts.join(' · ')}</p> : null}
        </div>
        {campaign.joined ? (
          <span className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-accent-100 px-3 text-xs font-semibold text-accent-800">
            <i className="ri-check-line" /> Joined
          </span>
        ) : (
          <Button size="sm" className="shrink-0" onClick={onJoin} disabled={busy}>
            {busy ? 'Joining…' : 'Join'}
          </Button>
        )}
      </div>
      {campaign.summary || campaign.deliverables ? (
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer text-xs font-medium text-primary-700 hover:text-primary-900">What's involved</summary>
          {campaign.summary ? <p className="mt-2 whitespace-pre-line text-foreground-800">{campaign.summary}</p> : null}
          {campaign.deliverables ? (
            <p className="mt-2 whitespace-pre-line text-foreground-800">
              <span className="font-medium">Deliverables: </span>
              {campaign.deliverables}
            </p>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}

function Done({ agencyName, name, joined, onSignOut }: { agencyName?: string; name: string; joined: string[]; onSignOut: () => void }) {
  return (
    <div className="py-2 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
        <i className="ri-check-line" aria-hidden="true" />
      </span>
      <h2 className="mt-3 font-heading text-lg font-semibold text-foreground-950">You're all set{name ? `, ${firstName(name)}` : ''}</h2>
      {joined.length ? (
        <>
          <p className="mt-1 text-sm text-foreground-600">You joined:</p>
          <ul className="mt-2 space-y-1 text-sm font-medium text-foreground-900">
            {joined.map((campaign) => (
              <li key={campaign}>{campaign}</li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-foreground-600">
            {agencyName ?? "Each campaign's agency"} will send you the brief and fee to accept. Keep an eye on your email and the Twiva app.
          </p>
        </>
      ) : (
        <p className="mt-1 text-sm text-foreground-600">
          Your Twiva account is ready. {agencyName ? `${agencyName} can invite you to its campaigns.` : 'Agencies can invite you to their campaigns.'}
        </p>
      )}
      <button type="button" onClick={onSignOut} className="mt-5 text-xs font-medium text-foreground-600 hover:text-foreground-900">
        Sign out
      </button>
    </div>
  );
}

const inputClass =
  'mt-1 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-50';

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block text-sm font-medium text-foreground-800">
      {label}
      {children}
      {hint ? <span className="mt-1 block text-xs font-normal text-foreground-500">{hint}</span> : null}
    </label>
  );
}

function Alert({ tone, children }: { tone: 'error' | 'info'; children: ReactNode }) {
  const styles = tone === 'error' ? 'border-primary-200 bg-primary-50 text-primary-800' : 'border-secondary-200 bg-secondary-50 text-secondary-800';
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${styles}`}>
      <i className={`${tone === 'error' ? 'ri-error-warning-line' : 'ri-information-line'} mt-0.5`} />
      <span>{children}</span>
    </div>
  );
}

function Badge({ icon, tone, children }: { icon: string; tone: 'accent' | 'neutral'; children: ReactNode }) {
  const styles = tone === 'accent' ? 'bg-accent-100 text-accent-800' : 'bg-background-200 text-foreground-700';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${styles}`}>
      <i className={icon} aria-hidden="true" />
      {children}
    </span>
  );
}
